import { DemoNotFound, DemoReadOnly, isDemoPath, resolve } from '../demo/demo'
import { isSharePath, rewrite as rewriteShare } from '../share/share'
import type { ApiErrorBody } from './types'

/**
 * Where the API lives. In development Vite proxies /api to the backend, so the
 * default empty base is correct; a deployment behind a different host sets
 * VITE_API_BASE.
 */
const API_BASE = import.meta.env.VITE_API_BASE ?? ''

const TOKEN_KEY = 'pecgoprof.token'

/**
 * ApiError carries the backend's structured failure so a caller can branch on
 * `code` rather than parsing a message.
 */
export class ApiError extends Error {
  readonly status: number
  readonly code: string
  readonly details: Record<string, string>
  readonly requestId?: string

  constructor(status: number, body: ApiErrorBody | null, fallback: string) {
    super(body?.error?.message ?? fallback)
    this.name = 'ApiError'
    this.status = status
    this.code = body?.error?.code ?? 'unknown'
    this.details = body?.error?.details ?? {}
    this.requestId = body?.request_id
  }

  /** True when the session is gone and the user has to sign in again. */
  get isUnauthorized(): boolean {
    return this.status === 401
  }

  /** True when a plan limit blocked the action. */
  get isQuota(): boolean {
    return this.code === 'quota_exceeded'
  }
}

export const tokenStore = {
  get(): string | null {
    return localStorage.getItem(TOKEN_KEY)
  },
  set(token: string): void {
    localStorage.setItem(TOKEN_KEY, token)
  },
  clear(): void {
    localStorage.removeItem(TOKEN_KEY)
  },
}

/** Called when the API rejects our token, so the app can send the user to the login page. */
let onUnauthorized: (() => void) | null = null

export function setUnauthorizedHandler(handler: (() => void) | null): void {
  onUnauthorized = handler
}

interface RequestOptions {
  method?: string
  body?: unknown
  /** A multipart body is sent as-is, without a JSON content type. */
  formData?: FormData
  signal?: AbortSignal
}

async function request<T>(path: string, options: RequestOptions = {}): Promise<T> {
  // The demo is served from frozen captures of this same API, so every page,
  // hook and component below this line runs unchanged. Intercepting at the
  // transport is what keeps the demo from becoming a second implementation
  // that drifts from the product it is advertising.
  if (isDemoPath(path)) {
    return demoRequest<T>(path, options)
  }

  // A shared profile is read through a token in the URL rather than a session.
  // Rewriting here rather than in the pages means the profile page renders a
  // share and a signed-in profile with the same code — the alternative is two
  // pages that drift apart.
  if (isSharePath(path)) {
    const shared = rewriteShare(path)
    if (!shared) {
      // Deliberately loud. A shared view asking for something the share API
      // cannot answer would otherwise fall through to the authenticated API
      // with no token and produce a confusing 401.
      throw new ApiError(404, null, 'this is not available in a shared profile')
    }
    if ((options.method ?? 'GET') !== 'GET') {
      throw new ApiError(403, null, 'a shared profile is read-only')
    }
    path = shared
  }

  const headers: Record<string, string> = {}

  const token = tokenStore.get()
  if (token) {
    headers.Authorization = `Bearer ${token}`
  }

  let body: BodyInit | undefined
  if (options.formData) {
    // Let the browser set the multipart boundary itself.
    body = options.formData
  } else if (options.body !== undefined) {
    headers['Content-Type'] = 'application/json'
    body = JSON.stringify(options.body)
  }

  const response = await fetch(`${API_BASE}/api/v1${path}`, {
    method: options.method ?? 'GET',
    headers,
    body,
    signal: options.signal,
  })

  if (response.status === 204) {
    return undefined as T
  }

  if (!response.ok) {
    const errorBody = await readJson<ApiErrorBody>(response)
    const error = new ApiError(response.status, errorBody, response.statusText)

    // A 401 means the token is gone or expired; anything else is the caller's
    // problem to handle.
    if (error.isUnauthorized) {
      tokenStore.clear()
      onUnauthorized?.()
    }
    throw error
  }

  return (await readJson<T>(response)) as T
}

/**
 * demoRequest answers from the fixtures, and refuses to pretend at writes.
 *
 * A write is not blocked by a permission check here — there is no server to
 * write to. Saying so plainly is better than a network error the visitor has to
 * interpret.
 */
async function demoRequest<T>(path: string, options: RequestOptions): Promise<T> {
  const method = options.method ?? 'GET'
  if (method !== 'GET') {
    throw new DemoReadOnly()
  }

  try {
    return resolve<T>(path)
  } catch (error) {
    if (error instanceof DemoNotFound) {
      throw new ApiError(404, null, error.message)
    }
    throw error
  }
}

async function readJson<T>(response: Response): Promise<T | null> {
  const text = await response.text()
  if (!text.trim()) {
    return null
  }
  try {
    return JSON.parse(text) as T
  } catch {
    return null
  }
}

/** Builds a query string, dropping empty values. */
export function query(params: Record<string, string | number | undefined | null>): string {
  const search = new URLSearchParams()
  for (const [key, value] of Object.entries(params)) {
    if (value !== undefined && value !== null && value !== '') {
      search.set(key, String(value))
    }
  }
  const encoded = search.toString()
  return encoded ? `?${encoded}` : ''
}

export const api = {
  get: <T>(path: string, signal?: AbortSignal) => request<T>(path, { signal }),
  post: <T>(path: string, body?: unknown) => request<T>(path, { method: 'POST', body }),
  patch: <T>(path: string, body?: unknown) => request<T>(path, { method: 'PATCH', body }),
  put: <T>(path: string, body?: unknown) => request<T>(path, { method: 'PUT', body }),
  delete: <T>(path: string) => request<T>(path, { method: 'DELETE' }),
  upload: <T>(path: string, formData: FormData) => request<T>(path, { method: 'POST', formData }),

  /** Downloads a raw profile, authenticated the same way as every other call. */
  async download(path: string): Promise<Blob> {
    const token = tokenStore.get()
    const response = await fetch(`${API_BASE}/api/v1${path}`, {
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    })
    if (!response.ok) {
      throw new ApiError(response.status, await readJson<ApiErrorBody>(response), response.statusText)
    }
    return response.blob()
  },
}
